import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { CalendarDays, ChevronDown, RefreshCw, Video, Loader2, Calendar, FileVideo, FileText, Link2 } from "lucide-react";

const TABS = [
  { id: "upcoming", label: "Upcoming", icon: Calendar, count: "meetings" },
  { id: "recordings", label: "Recordings", icon: FileVideo, count: "recordings" },
  { id: "briefs", label: "Briefs", icon: FileText, amber: true },
  { id: "connect", label: "Connect", icon: Link2 },
];

const tabClass = (active, amber) => {
  if (!active) return "bg-white/5 text-gray-400 hover:text-white border border-white/10";
  return amber
    ? "bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-400 border border-amber-500/30"
    : "bg-gradient-to-r from-ism-teal/20 to-ism-teal/20 text-ism-teal border border-ism-teal/30";
};

export const CalendarHeader = ({ isExpanded, meetingCount, creatingMeeting, onRefresh, onCreateMeeting }) => (
  <div className="mb-4">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-4">
        <div className="p-3 rounded-xl bg-gradient-to-br from-ism-teal/20 to-ism-teal/10 border border-ism-teal/20">
          <CalendarDays className="h-6 w-6 text-ism-teal" />
        </div>
        <div>
          <h2 className="text-xl text-white tracking-tight flex items-center gap-2">
            <span className="text-ism-teal">Calendar & Meetings</span>
            {meetingCount > 0 && (
              <div className="px-2 py-0.5 rounded-full bg-ism-teal/20 border border-ism-teal/30">
                <span className="text-xs text-ism-teal font-medium" data-testid="meeting-count">{meetingCount}</span>
              </div>
            )}
          </h2>
          <p className="text-sm text-gray-500">
            {isExpanded ? (meetingCount > 0 ? `${meetingCount} upcoming this week` : "No upcoming meetings") : `${meetingCount} meetings this week`}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <AnimatePresence>
          {isExpanded && (
            <>
              <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}>
                <Button onClick={(e) => { e.stopPropagation(); onRefresh(); }} size="sm" className="bg-white/5 hover:bg-white/10 border border-white/10 text-gray-400 hover:text-white h-8 px-3 rounded-lg" data-testid="calendar-refresh-btn">
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </motion.div>
              <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button onClick={(e) => { e.stopPropagation(); onCreateMeeting(); }} disabled={creatingMeeting} size="sm" className="bg-gradient-to-r from-ism-teal to-ism-teal hover:from-ism-teal hover:to-ism-teal-light text-white h-8 px-3 rounded-lg border-0" data-testid="create-zoom-btn">
                  {creatingMeeting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Video className="h-4 w-4 mr-1" /> Create Zoom</>}
                </Button>
              </motion.div>
            </>
          )}
        </AnimatePresence>
        <motion.div animate={{ rotate: isExpanded ? 180 : 0 }} transition={{ duration: 0.3 }} className="p-1.5 rounded-lg bg-white/5">
          <ChevronDown className="h-5 w-5 text-gray-400" />
        </motion.div>
      </div>
    </div>
  </div>
);

export const CalendarTabs = ({ activeTab, onChange, counts }) => (
  <motion.div className="flex gap-2 mb-4 flex-wrap" onClick={(e) => e.stopPropagation()} initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
    {TABS.map(({ id, label, icon: Icon, count, amber }) => (
      <motion.button
        key={id}
        onClick={() => onChange(id)}
        className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm transition-all ${tabClass(activeTab === id, amber)}`}
        whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
        data-testid={`priorities-tab-${id}`}
      >
        <Icon className="h-4 w-4" />
        {label}{count ? ` (${counts[count]})` : ""}
      </motion.button>
    ))}
  </motion.div>
);
